import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { Loading, LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Receipt, Percent, Save, RotateCcw, Users, Briefcase, Clock, Rocket, Gift, ArrowUpToLine, Plus, X, SlidersHorizontal, CreditCard, Landmark } from "lucide-react";

const TIER_LABELS = { standard: "Standard", preferred: "Preferred", elite: "Pro", verified: "Verified", premier: "Premier", marksman: "Marksman", sharpshooter: "Sharpshooter", expert: "Expert" };

function RateTable({ title, icon: Icon, keys, values, onChange, testId, readOnly = false, note }) {
  return (
    <div className="border border-sniper bg-surface p-5" data-testid={testId}>
      <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
        <Icon className="w-4 h-4 text-olive-bright" /> {title}
      </h3>
      <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">{note || "Platform fee taken from the seller payout at sale time."}</p>
      <div className="mt-4 space-y-3">
        {keys.map((k) => (
          <div key={k} className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-secondary-foreground uppercase">{TIER_LABELS[k] || k}</span>
            <div className="relative">
              {readOnly ? (
                <div data-testid={`rate-${testId}-${k}`}
                  className="w-28 border border-sniper/50 bg-inset pl-3 pr-8 py-2 text-right font-mono text-sm text-muted-foreground">
                  {values[k] ?? "—"}
                  <Percent className="w-3.5 h-3.5 text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2" />
                </div>
              ) : (
                <>
                  <input
                    type="number" min="0" max="100" step="0.1"
                    data-testid={`rate-${testId}-${k}`}
                    value={values[k] ?? ""}
                    onChange={(e) => onChange(k, e.target.value)}
                    className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-3 pr-8 py-2 text-foreground font-mono text-sm text-right transition-colors"
                  />
                  <Percent className="w-3.5 h-3.5 text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2" />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PanelOwnedBanner({ what }) {
  return (
    <Link to="/admin/tier-permissions" data-testid="fees-panel-owned-banner"
      className="flex items-center gap-2 border border-olive/40 bg-olive/5 hover:bg-olive/10 text-olive-bright font-mono uppercase text-[11px] px-3 py-2.5 transition-colors">
      <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" /> {what} — edited in Admin → Configuration → Tier Permissions
    </Link>
  );
}

export default function AdminFees() {
  const [cfg, setCfg] = useState(null);
  const [individual, setIndividual] = useState({});
  const [dealer, setDealer] = useState({});
  const [escrow, setEscrow] = useState({ delivered_release_hours: 24, untracked_release_days: 7 });
  const [boost, setBoost] = useState({ boost_fee_7d: 4.99, boost_fee_14d: 8.99 });
  const [boostCredits, setBoostCredits] = useState({ individual: {}, dealer: {} });
  const [bumpCfg, setBumpCfg] = useState({ bump_fee: 0.5, bump_cooldown_hours: 24 });
  const [platformMinFee, setPlatformMinFee] = useState(1.0);
  const [processing, setProcessing] = useState({ processing_card_pct: 2.9, processing_card_fixed_cents: 30, processing_ach_pct: 0.8, processing_ach_cap_cents: 500 });
  const [packs, setPacks] = useState([10, 25, 50]);
  const [fees, setFees] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState(null);

  const load = async () => {
    setLoadErr(null);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const { data } = await api.get("/admin/commission-config", { signal: controller.signal });
      clearTimeout(timer);
      setCfg(data);
      setIndividual({ ...data.commission_individual });
      setDealer({ ...data.commission_dealer });
      setEscrow({ ...(data.escrow || { delivered_release_hours: 24, untracked_release_days: 7 }) });
      setBoost({ boost_fee_7d: data.boost_fee_7d ?? 4.99, boost_fee_14d: data.boost_fee_14d ?? 8.99 });
      setBoostCredits({ individual: { ...(data.boost_credits_individual || {}) }, dealer: { ...(data.boost_credits_dealer || {}) } });
      setBumpCfg({ bump_fee: data.bump_fee ?? 0.5, bump_cooldown_hours: data.bump_cooldown_hours ?? 24 });
      setPlatformMinFee(data.platform_min_fee ?? 1.0);
      setProcessing({
        processing_card_pct: data.processing_card_pct ?? 2.9,
        processing_card_fixed_cents: data.processing_card_fixed_cents ?? 30,
        processing_ach_pct: data.processing_ach_pct ?? 0.8,
        processing_ach_cap_cents: data.processing_ach_cap_cents ?? 500,
      });
      setPacks((data.credit_packs || [1000, 2500, 5000]).map((c) => c / 100));
      api.get("/config/fees").then(({ data }) => setFees(data)).catch(() => {});
    } catch (err) {
      clearTimeout(timer);
      setLoadErr(formatError(err.response?.data?.detail) || (controller.signal.aborted ? "Timed out after 10s — the server did not respond." : "Couldn't load platform fees."));
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/commission-config", {
        commission_dealer: Object.fromEntries(Object.entries(dealer).map(([k, v]) => [k, Number(v)])),
        escrow: {
          delivered_release_hours: Number(escrow.delivered_release_hours),
          untracked_release_days: Number(escrow.untracked_release_days),
        },
        boost_fee_7d: Number(boost.boost_fee_7d),
        boost_fee_14d: Number(boost.boost_fee_14d),
        boost_credits_individual: Object.fromEntries(Object.entries(boostCredits.individual).map(([k, v]) => [k, Math.max(0, parseInt(v || 0, 10))])),
        boost_credits_dealer: Object.fromEntries(Object.entries(boostCredits.dealer).map(([k, v]) => [k, Math.max(0, parseInt(v || 0, 10))])),
        bump_fee: Number(bumpCfg.bump_fee),
        bump_cooldown_hours: Math.max(0, parseInt(bumpCfg.bump_cooldown_hours || 0, 10)),
        platform_min_fee: Number(platformMinFee),
        processing_card_pct: Number(processing.processing_card_pct),
        processing_card_fixed_cents: Math.max(0, parseInt(processing.processing_card_fixed_cents || 0, 10)),
        processing_ach_pct: Number(processing.processing_ach_pct),
        processing_ach_cap_cents: Math.max(0, parseInt(processing.processing_ach_cap_cents || 0, 10)),
        credit_packs: packs.map((d) => Math.round(Number(d) * 100)).filter((c) => c >= 100),
      });
      setIndividual({ ...data.commission_individual });
      setDealer({ ...data.commission_dealer });
      setEscrow({ ...(data.escrow || escrow) });
      setBoost({ boost_fee_7d: data.boost_fee_7d ?? boost.boost_fee_7d, boost_fee_14d: data.boost_fee_14d ?? boost.boost_fee_14d });
      setBoostCredits({ individual: { ...(data.boost_credits_individual || boostCredits.individual) }, dealer: { ...(data.boost_credits_dealer || boostCredits.dealer) } });
      setBumpCfg({ bump_fee: data.bump_fee ?? bumpCfg.bump_fee, bump_cooldown_hours: data.bump_cooldown_hours ?? bumpCfg.bump_cooldown_hours });
      setPlatformMinFee(data.platform_min_fee ?? platformMinFee);
      if (data.processing_card_pct != null) setProcessing({
        processing_card_pct: data.processing_card_pct ?? processing.processing_card_pct,
        processing_card_fixed_cents: data.processing_card_fixed_cents ?? processing.processing_card_fixed_cents,
        processing_ach_pct: data.processing_ach_pct ?? processing.processing_ach_pct,
        processing_ach_cap_cents: data.processing_ach_cap_cents ?? processing.processing_ach_cap_cents,
      });
      setPacks((data.credit_packs || packs.map((d) => Math.round(d * 100))).map((c) => c / 100));
      toast.success("Settings saved. New sales, marketplace timers, bump price and boost prices use these immediately.");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!cfg) {
    if (loadErr) return <LoadFailed reason={`Couldn't load platform fees — ${loadErr}`} onRetry={load} testid="admin-fees-load-failed" />;
    return <Loading label="Loading platform fees…" testid="admin-fees-loading" />;
  }

  return (
    <div className="text-foreground" data-testid="admin-fees">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Receipt className="w-7 h-7 text-olive-bright" /> Platform Fees</h2>
      <p className="text-sm font-body text-secondary-foreground mt-2">Dealer rates, marketplace timers, boost &amp; bump pricing and credit packs. Individual member commission is read-only here — it's owned by the Tier Permissions panel.</p>
      <div className="mt-3"><PanelOwnedBanner what="Individual commission &amp; membership tiers" /></div>
      <Crosshair className="my-5" />

      <div className="grid md:grid-cols-2 gap-4">
        <RateTable title="Individual Members" icon={Users} keys={(cfg.individual_tiers || []).filter((k) => k !== "preferred")} values={individual} testId="individual" readOnly note="Read-only — set per tier in Tier Permissions." />
        <RateTable title="Dealer Accounts" icon={Briefcase} keys={cfg.dealer_levels} values={dealer} onChange={(k, v) => setDealer((p) => ({ ...p, [k]: v }))} testId="dealer" />
      </div>

      <div className="mt-4 border border-volt/50 bg-volt/5 p-5" data-testid="min-fee-config">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <Receipt className="w-4 h-4 text-volt" /> Minimum Platform Fee
        </h3>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">The fee on every sale is the GREATER OF (tier rate × price) or this floor — applies to all tiers, including 0%. Stops small sales losing money to Stripe's processing fee.</p>
        <div className="mt-4 flex items-center justify-between gap-3 max-w-sm">
          <span className="font-mono text-sm text-secondary-foreground uppercase">Minimum fee per sale</span>
          <div className="relative shrink-0">
            <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
            <input type="number" min="0" max="10000" step="0.01" data-testid="platform-min-fee-input"
              value={platformMinFee ?? ""} onChange={(e) => setPlatformMinFee(e.target.value)}
              className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
          </div>
        </div>
        <p className="font-mono text-[10px] uppercase text-muted-foreground mt-2">
          At a {Number(individual.standard || 15)}% base rate, the floor governs any sale under ~${(Number(platformMinFee || 1) / (Number(individual.standard || 15) / 100)).toFixed(0)}.
        </p>
      </div>

      <div className="mt-4 border border-sniper bg-surface p-5" data-testid="processing-config">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-olive-bright" /> Payment Processing (buyer pays)
        </h3>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">
          The buyer covers payment processing ON TOP of the sale price — it is never deducted from the seller payout. Keep these matched to Stripe's published pricing. Card is grossed up so the charge covers Stripe's fee on the full total; the real fee is read off each charge and any gap is the platform's. ACH mirrors Stripe (percent of the charge, capped).
        </p>
        <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground mb-2"><CreditCard className="w-3.5 h-3.5" /> Card</div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-sm text-secondary-foreground uppercase">Rate</span>
              <div className="relative shrink-0">
                <input type="number" min="0" max="20" step="0.1" data-testid="processing-card-pct"
                  value={processing.processing_card_pct ?? ""} onChange={(e) => setProcessing((p) => ({ ...p, processing_card_pct: e.target.value }))}
                  className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-3 pr-8 py-2 text-foreground font-mono text-sm text-right transition-colors" />
                <Percent className="w-3.5 h-3.5 text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mt-2">
              <span className="font-mono text-sm text-secondary-foreground uppercase">Fixed</span>
              <div className="relative shrink-0">
                <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-xs">¢</span>
                <input type="number" min="0" max="1000" step="1" data-testid="processing-card-fixed"
                  value={processing.processing_card_fixed_cents ?? ""} onChange={(e) => setProcessing((p) => ({ ...p, processing_card_fixed_cents: e.target.value }))}
                  className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
              </div>
            </div>
          </div>
          <div>
            <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase text-muted-foreground mb-2"><Landmark className="w-3.5 h-3.5" /> Bank transfer (ACH)</div>
            <div className="flex items-center justify-between gap-3">
              <span className="font-mono text-sm text-secondary-foreground uppercase">Rate</span>
              <div className="relative shrink-0">
                <input type="number" min="0" max="20" step="0.1" data-testid="processing-ach-pct"
                  value={processing.processing_ach_pct ?? ""} onChange={(e) => setProcessing((p) => ({ ...p, processing_ach_pct: e.target.value }))}
                  className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-3 pr-8 py-2 text-foreground font-mono text-sm text-right transition-colors" />
                <Percent className="w-3.5 h-3.5 text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2" />
              </div>
            </div>
            <div className="flex items-center justify-between gap-3 mt-2">
              <span className="font-mono text-sm text-secondary-foreground uppercase">Cap</span>
              <div className="relative shrink-0">
                <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
                <input type="number" min="0" max="1000" step="0.01" data-testid="processing-ach-cap"
                  value={((processing.processing_ach_cap_cents ?? 0) / 100).toString()}
                  onChange={(e) => setProcessing((p) => ({ ...p, processing_ach_cap_cents: Math.round(Number(e.target.value || 0) * 100) }))}
                  className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 border border-sniper bg-surface p-5" data-testid="escrow-config">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <Clock className="w-4 h-4 text-olive-bright" /> Marketplace Auto-Release
        </h3>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">Safety net that releases held funds to the seller if the buyer goes silent. A dispute always pauses these timers.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-mono text-sm text-secondary-foreground uppercase">Tracked — after delivery</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">UPS / FedEx / USPS delivery confirmed</div>
            </div>
            <div className="relative shrink-0">
              <input type="number" min="1" max="720" step="1" data-testid="escrow-delivered-hours"
                value={escrow.delivered_release_hours ?? ""}
                onChange={(e) => setEscrow((p) => ({ ...p, delivered_release_hours: e.target.value }))}
                className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-3 pr-12 py-2 text-foreground font-mono text-sm text-right transition-colors" />
              <span className="text-[10px] font-mono uppercase text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2">hrs</span>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="font-mono text-sm text-secondary-foreground uppercase">Untracked / pickup — after ship</div>
              <div className="text-[10px] font-mono uppercase text-muted-foreground">No tracking / local hand-off</div>
            </div>
            <div className="relative shrink-0">
              <input type="number" min="1" max="90" step="1" data-testid="escrow-untracked-days"
                value={escrow.untracked_release_days ?? ""}
                onChange={(e) => setEscrow((p) => ({ ...p, untracked_release_days: e.target.value }))}
                className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-3 pr-12 py-2 text-foreground font-mono text-sm text-right transition-colors" />
              <span className="text-[10px] font-mono uppercase text-muted-foreground absolute right-2.5 top-1/2 -translate-y-1/2">days</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4 border border-sniper bg-surface p-5" data-testid="boost-config">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2">
          <Rocket className="w-4 h-4 text-volt" /> Boosted Listings
        </h3>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">Flat one-off fee for premium feed placement (2 slots/page). Change either price anytime — it applies to new boosts instantly.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-4">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-secondary-foreground uppercase">7-day price</span>
            <div className="relative shrink-0">
              <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
              <input type="number" min="0" max="10000" step="0.01" data-testid="boost-fee-7d"
                value={boost.boost_fee_7d ?? ""}
                onChange={(e) => setBoost((p) => ({ ...p, boost_fee_7d: e.target.value }))}
                className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-secondary-foreground uppercase">14-day price</span>
            <div className="relative shrink-0">
              <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
              <input type="number" min="0" max="10000" step="0.01" data-testid="boost-fee-14d"
                value={boost.boost_fee_14d ?? ""}
                onChange={(e) => setBoost((p) => ({ ...p, boost_fee_14d: e.target.value }))}
                className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center gap-2 text-olive-bright">
          <Gift className="w-3.5 h-3.5" />
          <span className="font-mono text-[11px] uppercase tracking-wide">Monthly free boost credits (7-day, don't roll over)</span>
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2">
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Individual members</div>
            {["standard", "preferred", "elite"].map((k) => (
              <div key={k} className="flex items-center justify-between gap-3 py-1">
                <span className="font-mono text-sm text-secondary-foreground uppercase">{TIER_LABELS[k] || k}</span>
                <input type="number" min="0" max="100" step="1" data-testid={`boost-credit-individual-${k}`}
                  value={boostCredits.individual[k] ?? 0}
                  onChange={(e) => setBoostCredits((p) => ({ ...p, individual: { ...p.individual, [k]: e.target.value } }))}
                  className="w-20 bg-input border border-sniper focus:border-olive outline-none px-3 py-1.5 text-foreground font-mono text-sm text-right transition-colors" />
              </div>
            ))}
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Dealer accounts</div>
            {["standard", "verified", "premier"].map((k) => (
              <div key={k} className="flex items-center justify-between gap-3 py-1">
                <span className="font-mono text-sm text-secondary-foreground uppercase">{TIER_LABELS[k] || k}</span>
                <input type="number" min="0" max="100" step="1" data-testid={`boost-credit-dealer-${k}`}
                  value={boostCredits.dealer[k] ?? 0}
                  onChange={(e) => setBoostCredits((p) => ({ ...p, dealer: { ...p.dealer, [k]: e.target.value } }))}
                  className="w-20 bg-input border border-sniper focus:border-olive outline-none px-3 py-1.5 text-foreground font-mono text-sm text-right transition-colors" />
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 border border-sniper bg-surface p-5" data-testid="bump-credits-config">
        <div className="flex items-center gap-2 text-olive-bright">
          <ArrowUpToLine className="w-4 h-4" />
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">Bump &amp; Prepaid Credits</h3>
        </div>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">Bump = recency refresh (top of Newest, no "boosted" label). Cooldown is the anti-spam control.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-secondary-foreground uppercase">Bump fee</span>
            <div className="relative shrink-0">
              <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
              <input type="number" min="0" max="10000" step="0.01" data-testid="bump-fee-input"
                value={bumpCfg.bump_fee ?? ""} onChange={(e) => setBumpCfg((p) => ({ ...p, bump_fee: e.target.value }))}
                className="w-28 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
            </div>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="font-mono text-sm text-secondary-foreground uppercase">Bump cooldown (hours)</span>
            <input type="number" min="0" max="8760" step="1" data-testid="bump-cooldown-input"
              value={bumpCfg.bump_cooldown_hours ?? ""} onChange={(e) => setBumpCfg((p) => ({ ...p, bump_cooldown_hours: e.target.value }))}
              className="w-28 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm text-right transition-colors" />
          </div>
        </div>
        <div className="mt-4">
          <div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Credit packs (USD)</div>
          <div className="flex flex-wrap items-center gap-2">
            {packs.map((d, i) => (
              <div key={i} className="relative">
                <span className="text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2 text-sm">$</span>
                <input type="number" min="1" max="10000" step="1" data-testid={`credit-pack-input-${i}`}
                  value={d} onChange={(e) => setPacks((p) => p.map((x, j) => (j === i ? e.target.value : x)))}
                  className="w-24 bg-input border border-sniper focus:border-olive outline-none pl-6 pr-2 py-2 text-foreground font-mono text-sm text-right transition-colors" />
                <button onClick={() => setPacks((p) => p.filter((_, j) => j !== i))} title="Remove pack" data-testid={`credit-pack-remove-${i}`}
                  className="ml-1 text-muted-foreground hover:text-blaze transition-colors align-middle"><X className="w-3.5 h-3.5 inline" /></button>
              </div>
            ))}
            <button onClick={() => setPacks((p) => [...p, 10])} data-testid="credit-pack-add"
              className="inline-flex items-center gap-1 border border-sniper hover:border-olive text-secondary-foreground hover:text-foreground font-mono uppercase text-[11px] px-3 py-2 transition-colors"><Plus className="w-3.5 h-3.5" /> Pack</button>
          </div>
          <p className="font-mono text-[10px] uppercase text-muted-foreground mt-2">At ${Number(bumpCfg.bump_fee || 0).toFixed(2)}/bump, a $10 pack ≈ {Math.floor(1000 / Math.max(1, Math.round(Number(bumpCfg.bump_fee || 0) * 100)))} bumps.</p>
        </div>
      </div>

      <div className="mt-5 flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="commission-save"
          className="inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save settings"}
        </button>
        <button onClick={() => { setIndividual({ ...cfg.commission_individual }); setDealer({ ...cfg.commission_dealer }); setEscrow({ ...(cfg.escrow || escrow) }); setBoost({ boost_fee_7d: cfg.boost_fee_7d, boost_fee_14d: cfg.boost_fee_14d }); setBoostCredits({ individual: { ...(cfg.boost_credits_individual || {}) }, dealer: { ...(cfg.boost_credits_dealer || {}) } }); setPlatformMinFee(cfg.platform_min_fee ?? 1.0); }} data-testid="commission-reset"
          className="inline-flex items-center gap-2 border border-sniper hover:border-white text-secondary-foreground hover:text-foreground font-mono uppercase text-sm px-4 py-2.5 transition-colors">
          <RotateCcw className="w-4 h-4" /> Revert
        </button>
      </div>

      {fees && (
        <div className="mt-8 border border-sniper bg-surface p-5" data-testid="marketplace-fees">
          <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">Marketplace Fees</h3>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 font-mono text-sm">
            <div><div className="text-[10px] uppercase text-muted-foreground">Free listings</div><div className="text-foreground">{fees.free_listings}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Post fee</div><div className="text-foreground">${fees.post_fee}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Bump fee</div><div className="text-foreground">${fees.bump_fee}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">Min fee / sale</div><div className="text-foreground">${Number(platformMinFee ?? 1).toFixed(2)}</div></div>
            <div><div className="text-[10px] uppercase text-muted-foreground">PRO / yr</div><div className="text-foreground">${fees.pro_price}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}
